import React, { Fragment, useEffect, useState } from "react";
import COLORS from "../../../assets/Colors";
import theme from "../../../assets/theme";
import styled, { keyframes, css } from "styled-components";
import useLoader from "../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import { getErrorMessage } from "../../../helpers/functions";
import CaseService from "../../../services/CaseService";
import { AssigninBulkCaseId } from "../../../helpers/validationSchema";
import { Formik } from "formik";
import FormField from "../../common/FormField/FormField";
import {
  PrimaryCTAButton,
  PrimaryOutlinedCTAButton,
} from "../../common/Buttons";
import { Modal } from "@material-ui/core";
import CaseManagerModal from "../AssignCaseManagerBulkCase/CaseManagerModal";
import AddIcon from "@material-ui/icons/Add";
import * as XLSX from "xlsx";
import ShowModal from "./ShowModal";

const inputModes = {
  MANUAL: "manual",
  EXCEL: "excel",
};

const inputs = [
  {
    type: "number",
    name: "startCaseId",
    label: "From CaseId",
    required: true,
    autoFocus: true,
  },
  {
    type: "number",
    name: "endCaseId",
    label: "End CaseId",
    required: true,
  },
  {
    type: "text",
    name: "exceptCaseIds",
    label: "Except Case IDs (comma separated)",
    required: false,
    placeholder: "e.g., 102, 109, 120",
    helperText: "Optional: exclude specific cases from the selected range",
  },
];

const Index = ({ modal, setModal }) => {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();

  const [cmList, setCmList] = useState(null);
  const [coManagers, setCoManagers] = useState([]);
  const [selectingFor, setSelectingFor] = useState("primary");

  const [state, setState] = useState(false);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [mode, setMode] = useState(inputModes.MANUAL);
  const [excelCaseIds, setExcelCaseIds] = useState([]);
  const [stateModal, setStateModal] = useState({ state: false });

  async function onFormSubmit(values, { setFieldValue }) {
    try {
      setLoader({ state: true });

      if (!cmList?.id) {
        enqueueSnackbar("Please select a primary Scrutiny Clerk", {
          variant: "warning",
        });
        return;
      }
      let payload = {};
      if (mode === inputModes.MANUAL) {
        payload = {
          startCaseId: Number(values.startCaseId),
          endCaseId: Number(values.endCaseId),
          exceptCaseIds: values.exceptCaseIds,
          resolutionKind: values?.resolutionKind,
          coManagerIds: coManagers.map((cm) => cm.id),
        };
      } else {
        if (excelCaseIds.length === 0) {
          enqueueSnackbar("Please upload a valid Excel file with case IDs", {
            variant: "warning",
          });
          return;
        }
        payload = {
          caseIds: excelCaseIds.map((id) => Number(id)),
          resolutionKind: values?.resolutionKind,
          coManagerIds: coManagers.map((cm) => cm.id),
        };
      }
      const response = await CaseService.CaseManagerIdBulkCase(
        payload,
        cmList.id,
      );
      if (response) {
        enqueueSnackbar(
          response.message || "Scrutiny Clerk(s) assigned successfully!",
          { variant: "success" },
        );
        setStateModal({ state: true, metaData: response });
        setModal({ state: false });
      }
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  }

  const handleFileUpload = (e, setFieldValue) => {
    const file = e.target.files[0];
    if (!file) return;
    setFieldValue("fileName", file.name);
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
        if (data.length === 0) {
          enqueueSnackbar("Excel file is empty", { variant: "error" });
          return;
        }
        const headers = (data[0] || []).map((h) =>
          (h || "").toString().toLowerCase().trim(),
        );
        const caseIdColIndex = headers.findIndex(
          (h) =>
            h === "caseid" ||
            h === "case id" ||
            h === "id" ||
            h === "case_id" ||
            h === "case-id" ||
            h === "Case ID/Dispute no",
        );

        if (caseIdColIndex === -1) {
          enqueueSnackbar(
            "Column not found. Please use one of: caseId, Case Id, id",
            { variant: "error" },
          );
          return;
        }
        const ids = data
          .slice(1)
          .map((row) => row[caseIdColIndex])
          .filter((id) => id !== undefined && id !== null && id !== "")
          .map((id) => Number(id))
          .filter((id) => !isNaN(id) && id > 0);

        if (ids.length === 0) {
          enqueueSnackbar("No valid case IDs found in the file", {
            variant: "warning",
          });
          return;
        }
        setExcelCaseIds(ids);
        enqueueSnackbar(
          `${ids.length} case ID${ids.length > 1 ? "s" : ""} loaded successfully`,
          { variant: "success" },
        );
      } catch (err) {
        console.error(err);
        enqueueSnackbar(
          "Failed to read Excel file. Please try a valid .xlsx file.",
          { variant: "error" },
        );
      }
    };
    reader.onerror = () => {
      enqueueSnackbar("Error reading file", { variant: "error" });
    };
    reader.readAsBinaryString(file);
    e.target.value = null;
  };

  useEffect(() => {
    if (modal?.state) {
      setCmList(null);
      setCoManagers([]);
      setStart("");
      setEnd("");
      setMode(inputModes.MANUAL);
      setExcelCaseIds([]);
      setSelectingFor("primary");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal?.state]);

  return (
    <Fragment>
      <Modal open={modal?.state} onClose={() => setModal({ state: false })}>
        <Overlay>
          <ModalCard>
            {/* Header */}
            <ModalHeader>
              <HeaderAccent />
              <HeaderContent>
                <IconBadge>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </IconBadge>
                <ModalTitle>Assign Scrutiny Clerk</ModalTitle>
              </HeaderContent>
              <CloseBtn
                onClick={() => setModal({ state: false })}
                aria-label="Close"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </CloseBtn>
            </ModalHeader>

            {/* Body */}
            <ModalBody>
              <Formik
                initialValues={{
                  startCaseId: start || "",
                  endCaseId: end || "",
                  exceptCaseIds: "",
                  fileName: "",
                  resolutionKind: "arbitration",
                }}
                onSubmit={onFormSubmit}
                validationSchema={
                  mode === inputModes.MANUAL ? AssigninBulkCaseId : undefined
                }
                enableReinitialize
                validateOnChange
                validateOnBlur
              >
                {({
                  values,
                  handleChange,
                  errors,
                  handleSubmit,
                  touched,
                  handleBlur,
                  setFieldValue,
                  isValid,
                }) => (
                  <StyledForm onSubmit={handleSubmit}>
                    {/* Mode Toggle */}
                    <SectionLabel>Input Method</SectionLabel>
                    <ModeToggle>
                      <ModeOption
                        active={mode === inputModes.MANUAL}
                        onClick={() => {
                          setMode(inputModes.MANUAL);
                          setExcelCaseIds([]);
                        }}
                        type="button"
                      >
                        <ModeIcon>
                          <svg
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </ModeIcon>
                        Enter ID Range
                      </ModeOption>
                      <ModeOption
                        active={mode === inputModes.EXCEL}
                        onClick={() => setMode(inputModes.EXCEL)}
                        type="button"
                      >
                        <ModeIcon>
                          <svg
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                            <line x1="16" y1="13" x2="8" y2="13" />
                            <line x1="16" y1="17" x2="8" y2="17" />
                            <polyline points="10 9 9 9 8 9" />
                          </svg>
                        </ModeIcon>
                        Upload Excel
                      </ModeOption>
                    </ModeToggle>

                    {/* Input Fields */}
                    <FieldsSection>
                      {mode === inputModes.MANUAL ? (
                        <>
                          {inputs.map((input, key) => (
                            <FormField
                              key={key}
                              white
                              {...{
                                input,
                                values,
                                errors,
                                touched,
                                handleBlur,
                                handleChange,
                                setFieldValue,
                              }}
                            />
                          ))}
                        </>
                      ) : (
                        <ExcelUploadZone>
                          <label
                            htmlFor="excel-upload"
                            style={{ cursor: "pointer", display: "block" }}
                          >
                            <HiddenInput
                              id="excel-upload"
                              type="file"
                              accept=".xlsx, .xls"
                              onChange={(e) =>
                                handleFileUpload(e, setFieldValue)
                              }
                            />
                            <UploadContent loaded={excelCaseIds.length > 0}>
                              {excelCaseIds.length > 0 ? (
                                <>
                                  <UploadCheckCircle>
                                    <svg
                                      width="28"
                                      height="28"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                  </UploadCheckCircle>
                                  <UploadMainText>
                                    {excelCaseIds.length} Case IDs Loaded
                                  </UploadMainText>
                                  <UploadSubText>
                                    Click to replace file
                                  </UploadSubText>
                                </>
                              ) : (
                                <>
                                  <UploadCloud>
                                    <svg
                                      width="32"
                                      height="32"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <polyline points="16 16 12 12 8 16" />
                                      <line x1="12" y1="12" x2="12" y2="21" />
                                      <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
                                    </svg>
                                  </UploadCloud>
                                  <UploadMainText>
                                    Drop your Excel file here
                                  </UploadMainText>
                                  <UploadSubText>
                                    or click to browse · Column must be named{" "}
                                    <code>caseId</code>
                                  </UploadSubText>
                                </>
                              )}
                            </UploadContent>
                          </label>
                        </ExcelUploadZone>
                      )}
                    </FieldsSection>

                    {/* Case Type */}
                    <FieldsSection>
                      {[
                        {
                          type: "select",
                          name: "resolutionKind",
                          label: "Case Type",
                          required: true,
                          menuItems: [
                            { label: "Arbitration", value: "arbitration" },
                            { label: "Mediation", value: "mediation" },
                            { label: "Negotiation", value: "negotiation" },
                          ],
                        },
                      ].map((input, key) => (
                        <FormField
                          key={key}
                          menuItems={input.menuItems}
                          white
                          {...{
                            input,
                            values,
                            errors,
                            touched,
                            handleBlur,
                            handleChange,
                            setFieldValue,
                          }}
                        />
                      ))}
                    </FieldsSection>

                    {/* Divider */}
                    <Divider />

                    {/* Primary Case Manager */}
                    <ManagerSection>
                      <ManagerSectionHeader>
                        <SectionLabel style={{ margin: 0 }}>
                          Primary Scrutiny Clerk
                        </SectionLabel>
                        <RequiredDot>Required</RequiredDot>
                      </ManagerSectionHeader>
                      <ManagerCard
                        onClick={() => {
                          setSelectingFor("primary");
                          setState(true);
                        }}
                        filled={!!cmList?.name}
                      >
                        <AddCircle filled={!!cmList?.name}>
                          {cmList?.name ? (
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                              <circle cx="12" cy="7" r="4" />
                            </svg>
                          ) : (
                            <AddIcon style={{ fontSize: 18 }} />
                          )}
                        </AddCircle>
                        <ManagerInfo>
                          {cmList?.name ? (
                            <>
                              <ManagerName>{cmList.name}</ManagerName>
                              <ManagerEmail>{cmList.email}</ManagerEmail>
                            </>
                          ) : (
                            <ManagerPlaceholder>
                              Select Primary Scrutiny Clerk
                            </ManagerPlaceholder>
                          )}
                        </ManagerInfo>
                        <ChevronIcon>
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="9 18 15 12 9 6" />
                          </svg>
                        </ChevronIcon>
                      </ManagerCard>
                    </ManagerSection>

                    {/* Co-Managers */}
                    <ManagerSection>
                      <ManagerSectionHeader>
                        <SectionLabel style={{ margin: 0 }}>
                          Co-Clerks
                        </SectionLabel>
                        <OptionalPill>Optional</OptionalPill>
                      </ManagerSectionHeader>
                      <CoManagerCard>
                        <AddCircle
                          onClick={() => {
                            setSelectingFor("co");
                            setState(true);
                          }}
                          style={{ flexShrink: 0, cursor: "pointer" }}
                        >
                          <AddIcon style={{ fontSize: 18 }} />
                        </AddCircle>
                        <CoManagerContent>
                          {coManagers.length > 0 ? (
                            <CoManagerTagList>
                              {coManagers.map((cm) => (
                                <CoManagerTag key={cm.id}>
                                  <TagAvatar>
                                    {cm.name.charAt(0).toUpperCase()}
                                  </TagAvatar>
                                  <TagName>{cm.name}</TagName>
                                  <TagRemove
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setCoManagers(
                                        coManagers.filter(
                                          (c) => c.id !== cm.id,
                                        ),
                                      );
                                    }}
                                  >
                                    <svg
                                      width="12"
                                      height="12"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="3"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <line x1="18" y1="6" x2="6" y2="18" />
                                      <line x1="6" y1="6" x2="18" y2="18" />
                                    </svg>
                                  </TagRemove>
                                </CoManagerTag>
                              ))}
                            </CoManagerTagList>
                          ) : (
                            <ManagerPlaceholder style={{ padding: "10px 0" }}>
                              Add co-clerks(optional)
                            </ManagerPlaceholder>
                          )}
                        </CoManagerContent>
                      </CoManagerCard>
                    </ManagerSection>

                    {/* Buttons */}
                    <FooterActions>
                      <PrimaryCTAButton
                        type="submit"
                        onClick={handleSubmit}
                        disabled={
                          !cmList?.id ||
                          (mode === inputModes.MANUAL &&
                            (!isValid ||
                              !values.startCaseId ||
                              !values.endCaseId)) ||
                          (mode === inputModes.EXCEL &&
                            excelCaseIds.length === 0)
                        }
                        style={{ flex: 1 }}
                      >
                        Update
                      </PrimaryCTAButton>
                      <PrimaryOutlinedCTAButton
                        onClick={() => setModal({ state: false })}
                        style={{ flex: 1 }}
                      >
                        Cancel
                      </PrimaryOutlinedCTAButton>
                    </FooterActions>
                  </StyledForm>
                )}
              </Formik>
            </ModalBody>
          </ModalCard>
        </Overlay>
      </Modal>

      {stateModal?.state && <ShowModal {...{ stateModal, setStateModal }} />}

      <CaseManagerModal
        state={state}
        setState={setState}
        cmList={cmList}
        setCmList={setCmList}
        coManagers={coManagers}
        setCoManagers={setCoManagers}
        selectingFor={selectingFor}
        setSelectingFor={setSelectingFor}
      />
    </Fragment>
  );
};

export default Index;

const fadeIn = keyframes`
  from { opacity: 0; transform: translate(-50%, -46%); }
  to   { opacity: 1; transform: translate(-50%, -50%); }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1300;
`;

const ModalCard = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: calc(100% - 24px);
  max-width: 640px;
  background: #ffffff;
  border-radius: 20px;
  box-shadow:
    0 24px 80px rgba(0, 0, 0, 0.18),
    0 4px 16px rgba(0, 0, 0, 0.08);
  outline: none;
  animation: ${fadeIn} 0.25s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  display: flex;
  flex-direction: column;
  max-height: 95vh;

  @media (max-width: 480px) {
    border-radius: 16px;
    max-height: 100vh;
    top: auto;
    bottom: 0;
    left: 0;
    right: 0;
    transform: none;
    width: 100%;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f2f5;
  position: relative;
  flex-shrink: 0;
`;

const HeaderAccent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  border-radius: 20px 20px 0 0;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
`;

const IconBadge = styled.div`
  width: 36px;
  height: 36px;
  background: linear-gradient(
    135deg,
    ${COLORS.BTN_GREEN}20,
    ${COLORS.BTN_GREEN}10
  );
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLORS.BTN_GREEN};
  flex-shrink: 0;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: #293461;
  font-family: ${theme.fonts.primaryFontExtraBold};
  letter-spacing: -0.3px;
`;

const CloseBtn = styled.button`
  width: 34px;
  height: 34px;
  border: none;
  background: #f4f6f8;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  transition: all 0.18s;
  flex-shrink: 0;

  &:hover {
    background: #fee2e2;
    color: #ef4444;
    transform: rotate(90deg);
  }
`;

/* ─── Body ────────────────────────────────────────────────────── */
const ModalBody = styled.div`
  overflow-y: auto;
  padding: 20px 24px 24px;
  flex: 1;

  @media (max-width: 480px) {
    padding: 16px 16px 24px;
  }

  scrollbar-width: thin;
  scrollbar-color: #e5e7eb transparent;
  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #e5e7eb;
    border-radius: 99px;
  }
`;

const StyledForm = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

/* ─── Section Label ───────────────────────────────────────────── */
const SectionLabel = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 11.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #9ca3af;
  margin-bottom: 10px;
`;

/* ─── Mode Toggle ─────────────────────────────────────────────── */
const ModeToggle = styled.div`
  display: flex;
  background: #f4f6f8;
  border-radius: 12px;
  padding: 4px;
  gap: 4px;
  margin-bottom: 20px;
`;

const ModeOption = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 10px 12px;
  border: none;
  border-radius: 9px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;

  ${(props) =>
    props.active
      ? css`
          background: #ffffff;
          color: ${COLORS.BTN_GREEN};
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          font-weight: 700;
        `
      : css`
          background: transparent;
          color: #6b7280;
          &:hover {
            background: rgba(255, 255, 255, 0.5);
          }
        `}

  @media (max-width: 360px) {
    font-size: 12px;
    padding: 10px 8px;
  }
`;

const ModeIcon = styled.span`
  display: flex;
  align-items: center;
`;

const FieldsSection = styled.div`
  margin-bottom: 16px;
  animation: ${slideUp} 0.2s ease both;
`;

const ExcelUploadZone = styled.div`
  border-radius: 14px;
  overflow: hidden;
  transition: border-color 0.2s;

  &:hover {
    border-color: ${COLORS.BTN_GREEN};
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const UploadContent = styled.div`
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  background: ${(props) =>
    props.loaded ? `${COLORS.BTN_GREEN}08` : "#fafafa"};
  transition: background 0.2s;

  &:hover {
    background: ${COLORS.BTN_GREEN}08;
  }
`;

const UploadCloud = styled.div`
  color: #9ca3af;
  margin-bottom: 4px;
`;

const UploadCheckCircle = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${COLORS.BTN_GREEN}20;
  color: ${COLORS.BTN_GREEN};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
`;

const UploadMainText = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const UploadSubText = styled.div`
  font-size: 12px;
  color: #9ca3af;
  font-family: ${theme.fonts.primaryFont};

  code {
    background: #f3f4f6;
    padding: 1px 5px;
    border-radius: 4px;
    font-family: monospace;
    font-size: 11px;
    color: ${COLORS.BTN_GREEN};
  }
`;

/* ─── Divider ─────────────────────────────────────────────────── */
const Divider = styled.div`
  height: 1px;
  background: linear-gradient(90deg, transparent, #e5e7eb, transparent);
  margin: 8px 0 20px;
`;

/* ─── Manager Sections ────────────────────────────────────────── */
const ManagerSection = styled.div`
  margin-bottom: 16px;
`;

const ManagerSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
`;

const RequiredDot = styled.span`
  font-size: 10px;
  font-weight: 700;
  color: #ef4444;
  background: #fee2e2;
  padding: 2px 7px;
  border-radius: 99px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const OptionalPill = styled.span`
  font-size: 10px;
  font-weight: 700;
  color: #6b7280;
  background: #f3f4f6;
  padding: 2px 7px;
  border-radius: 99px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ManagerCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1.5px solid
    ${(props) => (props.filled ? `${COLORS.BTN_GREEN}40` : "#e5e7eb")};
  background: ${(props) =>
    props.filled ? `${COLORS.BTN_GREEN}05` : "#fafafa"};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${COLORS.BTN_GREEN};
    background: ${COLORS.BTN_GREEN}08;
  }
`;

const CoManagerCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1.5px solid #e5e7eb;
  background: #fafafa;
  min-height: 56px;
`;

const AddCircle = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${(props) =>
    props.filled ? `${COLORS.BTN_GREEN}20` : `${COLORS.BTN_GREEN}15`};
  color: ${COLORS.BTN_GREEN};
  transition: all 0.2s;

  &:hover {
    background: ${COLORS.BTN_GREEN}30;
    transform: scale(1.05);
  }
`;

const ManagerInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ManagerName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1a2035;
  font-family: ${theme.fonts.primaryFontSemiBold};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ManagerEmail = styled.div`
  font-size: 12px;
  color: #6b7280;
  font-family: ${theme.fonts.primaryFont};
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ManagerPlaceholder = styled.div`
  font-size: 13px;
  color: #9ca3af;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const ChevronIcon = styled.div`
  color: #9ca3af;
  flex-shrink: 0;
`;

const CoManagerContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const CoManagerTagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 2px 0;
`;

const CoManagerTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${COLORS.BTN_GREEN}15;
  border: 1px solid ${COLORS.BTN_GREEN}30;
  padding: 5px 10px 5px 6px;
  border-radius: 99px;
  max-width: 100%;
`;

const TagAvatar = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${COLORS.BTN_GREEN};
  color: white;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${theme.fonts.primaryFontExtraBold};
  flex-shrink: 0;
`;

const TagName = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${COLORS.BTN_GREEN};
  font-family: ${theme.fonts.primaryFontSemiBold};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
`;

const TagRemove = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  cursor: pointer;
  color: ${COLORS.BTN_GREEN};
  background: ${COLORS.BTN_GREEN}20;
  flex-shrink: 0;
  transition: all 0.15s;

  &:hover {
    background: #fee2e2;
    color: #ef4444;
  }
`;

/* ─── Footer ──────────────────────────────────────────────────── */
const FooterActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #f0f2f5;

  @media (max-width: 400px) {
    flex-direction: column-reverse;
  }
`;
